import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from 'react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useGetSegments } from '@/api/segments/useGetSegments';
import { useAppSelector } from '@/app/hooks';
import { Button } from '@/component-library/primitives/Button/Button';
import Loader from '@/component-library/primitives/Loader/Loader';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import { QuickFilter, QuickFilterCriteria } from '@/component-library/widgets/QuickFilter/QuickFilter';
import SearchField from '@/component-library/widgets/SearchField/SearchField';
import Table from '@/component-library/widgets/Table/Table';
import { usePageTracking } from '@/hooks/useTracking';
import { Member } from '@/models/AudienceByWorkspace';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { HttpMethodsEnum, PermissionsEnum } from '@/modules/shared/enums/enums';
import usePermissionChecker from '@/modules/shared/hooks/usePermissionChecker';
import { AwsConfigApiGateway } from '@/utils/config/api';
import { AppRoutes } from '@/utils/routes/router';

import { useSegmentsTable } from './useSegmentsTable';

const Segments = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Type instantiation depth issue with i18next
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useGetSegments();
  const { columns, deleteConfirmationModal } = useSegmentsTable();
  const isActionAllowed = usePermissionChecker(PermissionsEnum.SEGMENTS, HttpMethodsEnum.POST);
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);

  // Track page view
  usePageTracking('segments');

  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });
  const [filterValue, setFilterValue] = useState('');
  const [quickFilterCriteria, setQuickFilterCriteria] = useState<QuickFilterCriteria>({});

  // Load segment details when quick filter is active to check users in segments
  const hasActiveQuickFilter = Boolean(
    quickFilterCriteria.riskLevel ||
      quickFilterCriteria.emailDomain ||
      quickFilterCriteria.riskScoreFrom !== null ||
      quickFilterCriteria.riskScoreTo !== null ||
      quickFilterCriteria.status ||
      quickFilterCriteria.signalActionType,
  );

  // Only load segment details when quick filter is active
  const segmentDetailsQueries = useQueries(
    data && hasActiveQuickFilter && !!workspace?.id && !!user?.signInUserSession?.idToken?.jwtToken
      ? data.map((segment) => ({
          queryKey: ['segmentDetails', workspace?.id, segment.id],
          queryFn: async () => {
            try {
              const response = await axios.get(
                `${AwsConfigApiGateway.pooledTenantApiUrl}/workspaces/${workspace?.id}/segments/${segment.id}`,
                {
                  headers: {
                    'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
                    Authorization: 'Bearer ' + user?.signInUserSession?.idToken?.jwtToken,
                  },
                },
              );
              return response.data.data;
            } catch (error: any) {
              // If request fails (e.g., 403 Forbidden), return null to indicate we couldn't load details
              // This allows the segment to still be shown in the list
              console.warn(
                `Failed to load details for segment ${segment.id}:`,
                error?.response?.status || error?.message,
              );
              return null;
            }
          },
          enabled:
            !!segment.id && !!workspace?.id && !!user?.signInUserSession?.idToken?.jwtToken && hasActiveQuickFilter,
          staleTime: 5 * 60 * 1000,
          cacheTime: 10 * 60 * 1000,
          retry: false, // Don't retry failed requests
        }))
      : [],
  );

  // Helper function to check if a member matches quick filter criteria
  const memberMatchesCriteria = (member: Member, criteria: QuickFilterCriteria): boolean => {
    const getRiskLevelFromScore = (riskScore: number) => {
      if (riskScore >= 60) return 'high';
      if (riskScore >= 30) return 'medium';
      return 'low';
    };

    const getStatusFromRow = (row: Member) => {
      const { subscribed, active, stripeCreatedAt, createdAt, parsedChurnRiskData } = row;
      const subscriptionStatus = parsedChurnRiskData?.subscriptionStatus;
      const engagementLevel = parsedChurnRiskData?.engagementLevel;
      const riskScore = parsedChurnRiskData?.riskScore ?? 0;

      const isNewUser = (() => {
        const createdDate = stripeCreatedAt ? new Date(stripeCreatedAt * 1000) : new Date(createdAt);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return createdDate > sevenDaysAgo && riskScore < 30 && engagementLevel === 'low';
      })();

      if (isNewUser) return 'new_user';
      if (subscriptionStatus === 'active' && subscribed && active) return 'active';
      if (subscriptionStatus === 'cancelled') return 'cancelled';
      if (subscriptionStatus === 'past_due') return 'past_due';
      if (subscriptionStatus === 'expired') return 'past_customer';
      if (subscriptionStatus === 'trialing') return 'free_trial';
      return 'free';
    };

    // Apply risk level filter
    if (criteria.riskLevel) {
      const score = member?.parsedChurnRiskData?.riskScore ?? 0;
      const level = getRiskLevelFromScore(score);
      if (level !== criteria.riskLevel) return false;
    }

    // Apply email domain filter
    if (criteria.emailDomain) {
      const email = member?.email || '';
      const domain = criteria.emailDomain.toLowerCase().replace('@', '');
      if (!email.toLowerCase().includes(`@${domain}`) && !email.toLowerCase().endsWith(`@${domain}`)) {
        return false;
      }
    }

    // Apply risk score range filter
    if (
      (criteria.riskScoreFrom !== null && criteria.riskScoreFrom !== undefined) ||
      (criteria.riskScoreTo !== null && criteria.riskScoreTo !== undefined)
    ) {
      const score = member?.parsedChurnRiskData?.riskScore ?? 0;
      if (criteria.riskScoreFrom !== null && criteria.riskScoreFrom !== undefined && score < criteria.riskScoreFrom)
        return false;
      if (criteria.riskScoreTo !== null && criteria.riskScoreTo !== undefined && score > criteria.riskScoreTo)
        return false;
    }

    // Apply status filter
    if (criteria.status) {
      const status = getStatusFromRow(member);
      if (status !== criteria.status) return false;
    }

    // Apply signal action type filter
    if (criteria.signalActionType === 'discount') {
      const recommendedActions = member?.recommendedActions || [];
      const parsedChurnRiskActions = member?.parsedChurnRiskData?.recommendedActions || [];
      const allActions = [...recommendedActions, ...parsedChurnRiskActions];

      const hasDiscountAction = allActions.some((action: string) => {
        const actionLower = action.toLowerCase();
        return actionLower.includes('discount') || actionLower.includes('promo') || actionLower.includes('offer');
      });

      if (!hasDiscountAction) return false;
    }

    return true;
  };

  const filteredData = useMemo(() => {
    const textFiltered =
      data?.filter((row) =>
        Object.values(row).some(
          (value) => value !== null && value.toString().toLowerCase().includes(filterValue.toLowerCase()),
        ),
      ) || [];

    // If quick filter is active, filter segments based on users that match criteria
    if (hasActiveQuickFilter) {
      // Check if any queries are still loading
      const isLoadingAny = segmentDetailsQueries.some((query) => query.isLoading);

      // If queries are still loading, show all text-filtered segments (will be filtered once loaded)
      if (isLoadingAny) {
        return textFiltered;
      }

      return textFiltered.filter((segment) => {
        // Find segment details for this segment
        const queryIndex = data?.findIndex((s) => s.id === segment.id) ?? -1;
        const segmentDetailsQuery = segmentDetailsQueries[queryIndex];
        const segmentDetails = segmentDetailsQuery?.data as { audience?: Member[] } | null | undefined;

        // If query failed (returned null) or is still loading, include the segment
        // This prevents segments from disappearing when we can't load their details (e.g., 403 Forbidden)
        if (segmentDetails === null || segmentDetailsQuery?.isLoading) {
          return true;
        }

        // If we successfully loaded data but there's no audience, exclude the segment
        if (!segmentDetails || !segmentDetails.audience || segmentDetails.audience.length === 0) {
          return false;
        }

        // Check if any user in the segment matches the criteria
        return segmentDetails.audience.some((member: Member) => memberMatchesCriteria(member, quickFilterCriteria));
      });
    }

    return textFiltered;
  }, [data, filterValue, quickFilterCriteria, segmentDetailsQueries, hasActiveQuickFilter]);

  const handleNavigate = (route: string, isActionAllowed: boolean) => {
    if (!isActionAllowed) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Type instantiation depth issue with i18next
      setToastInfo({ message: t('CommonErrors.actionNotAllowed'), toastType: 'error' });
      setOpen(true);
      return;
    }
    navigate(route);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row items-center gap-3 mb-4">
        <div className="flex-grow justify-start">
          <SearchField initialValue={filterValue} setSearchValue={setFilterValue} />
        </div>
        <QuickFilter
          onApply={(criteria) => {
            setQuickFilterCriteria(criteria);
          }}
          onClear={() => {
            setQuickFilterCriteria({});
          }}
          activeCriteria={quickFilterCriteria}
          presets={[
            {
              id: 'high-risk-instagram',
              name: 'High Risk + Instagram',
              description: 'Segments containing users with HIGH RISK and @instagram.com email domain',
              criteria: {
                riskLevel: 'high',
                emailDomain: 'instagram.com',
              },
            },
            {
              id: 'risk-score-60-85',
              name: 'Risk Score 60-85%',
              description: 'Segments containing users with risk score between 60% and 85%',
              criteria: {
                riskScoreFrom: 60,
                riskScoreTo: 85,
              },
            },
            {
              id: 'discount-signals',
              name: 'Discount Action Signals',
              description: 'Segments containing users with signals that can be resolved with discount',
              criteria: {
                signalActionType: 'discount',
              },
            },
            {
              id: 'high-risk-active',
              name: 'High Risk Active Users',
              description: 'Segments containing active users with high churn risk',
              criteria: {
                riskLevel: 'high',
                status: 'active',
              },
            },
          ]}
        />
        {filteredData && filteredData.length > 0 && (
          <div className="justify-end">
            <Button className="!w-[10.5rem]" onClick={() => handleNavigate(AppRoutes.createSegment, isActionAllowed)}>
              {t('SegmentsPage.buttonNewSegment')}
            </Button>
          </div>
        )}
      </div>
      {deleteConfirmationModal}
      {isLoading ? (
        <Loader />
      ) : filteredData && filteredData.length > 0 ? (
        <Table
          columns={columns}
          data={filteredData ?? []}
          className={{
            cell: 'overflow-visible h-[7rem]',
          }}
        />
      ) : (
        <NoDataBoard
          description={t('NoDataBoard.addYourFirstSegment')}
          actionButton={
            <Button
              className="!w-[10.5rem] mt-4"
              data-cy="newSegmentButton"
              onClick={() => handleNavigate(AppRoutes.createSegment, isActionAllowed)}
            >
              {t('SegmentsPage.buttonNewSegment')}
            </Button>
          }
        />
      )}
      <ProvideToasts>
        <Toast
          isOpen={open}
          setOpen={setOpen}
          message={toastInfo.message}
          toastType={toastInfo.toastType as ToastType}
        />
      </ProvideToasts>
    </div>
  );
};

export default Segments;
